/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { CancelPresentationRounded, ChevronLeft, ChevronRight, ShortcutOutlined, SignalCellularAlt, Square } from '@mui/icons-material';
import CanvasJSReact from '../../../ui/canvasjs/canvasjs.react';
import PropTypes from 'prop-types';
import styles from './styles';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import { HYDRAULICS_CHART_COLORS, UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import { useDispatch, useSelector } from 'react-redux';
import Datagrid from '../../Datagrid';
import { downloadDataReport, downloadECDESDChartPDF, listHydraulicsDetails, listHydraulicsRunsResults } from '../../../../store/actions/hydraulics';
import ChartsAxisPopover from '../../ChartsAxisPopover';
import { getMinMaxOfKey } from '../../../../utils/objectHelpers';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import PumpRampModel from '../PumpRampModel';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import ReportInputsModal from '../ReportInputsModal';
import { HYDRAULICS_RUN_TYPES, REPORT_INPUT_LIST, TOOLTIPS } from '../../../../utils/constants';
import HydraulicsDetailsModal from '../HydraulicsRunModalDialog';
import { listWindow } from '../../../../store/actions/wellsWindow';
import ReportsDownloadSelector from '../ReportsDownloadSelector';
import VersionText from '../VersionText';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function getMatchingConfigsBySelectedKey(configs, selectedKey) {
  const selectedKeyValue = selectedKey?.key;

  const columnFieldKeys = selectedKey?.columnFields
    ? selectedKey.columnFields.map(col => col.field)
    : [];

  let extraKeys = [];

  if (['summary', 'esd_with_sbp', 'ecd_with_sbp'].includes(selectedKeyValue)) {
    extraKeys = ['pp', 'fp'];
  } else if (selectedKeyValue === 'pwd_ecd') {
    extraKeys = ['pp', 'fp', 'esd_with_current_sbp', 'ecd_with_sbp'];
  }

  const allowedKeys = new Set([
    ...columnFieldKeys,
    ...extraKeys
  ]);

  const matched = configs.filter(cfg => allowedKeys.has(cfg.key));

  const priorityOrder = ['pp', 'ann_mw'];

  const priority = [];
  const rest = [];

  matched.forEach(cfg => {
    if (priorityOrder.includes(cfg.key)) {
      priority.push(cfg);
    } else {
      rest.push(cfg);
    }
  });

  return [...priority, ...rest];
}

function updateSelectionNames(configs) {
  configs.forEach(cfg => {
    cfg.name = cfg.name.replace(/\s*\((Connection|Drilling)\)/g, '');
  });
  return configs;
}

function DepthVsDensityChartDialog({ onCloseClick, isOpen, data, wellDetails, rowData, hydraulicWellsDetails }) {
  const classes = styles();
  const { hydraulicsRunResults } = useSelector((state) => state.hydraulics);
  const chartData = React.useMemo(() => {
    return hydraulicsRunResults?.data?.results?.depth_points ? [...hydraulicsRunResults.data.results.depth_points] : [];
  }, [hydraulicsRunResults?.data?.results?.depth_points]);
  const pwdData = Array.isArray(hydraulicsRunResults?.data?.results?.pwd) ? [...hydraulicsRunResults.data.results.pwd] : [];
  const window = useSelector((state) => state.wellsWindow);
  const hydraulics = useSelector((state) => state.hydraulics);
  const [selectionMenu, setSelectionMenu] = useState([]);
  const [charttable, setCharttable] = useState('');
  const [tableRecord, setTableRecord] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const depthPoints = hydraulicsRunResults?.units?.results?.depth_points;
  const pwd = hydraulicsRunResults?.units?.results?.pwd;
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [showPumpRamp, setShowPumpRamp] = useState(false);
  const { unit } = useUnitConversion();
  const [showReportInput, setShowReportInputs] = useState(false);
  const [tableList, setTableList] = useState(REPORT_INPUT_LIST.map(item => item.key));
  const [reportPreview, setReportPreview] = useState(false);

  const targetData = hydraulicsRunResults?.data?.results?.inputs?.target_data;
  const isTargetDataEmpty = !targetData || Object.keys(targetData).length === 0 || !targetData?.anchor_point || targetData?.anchor_point === 'None';
  const [showHydraulicsParameterDialog, setShowHydraulicsParameterDialog] = useState({
    open: false,
    data: {},
    dialogParameter: ''
  });

  useEffect(() => {
    if (rowData?.isHydraulicsRunResults) {
      const body = {
        runId: rowData?._id,
        wellsInfoId: rowData?.wellsInfoId
      };
      dispatch(listHydraulicsRunsResults(body));
    }
    return () => {
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_RUN_RESULT_LIST_RESET
      });
    };
  }, [rowData]);

  useEffect(() => {
    if (!window?.data.length) {
      dispatch(listWindow(rowData?.wellsInfoId));
    }
  }, []);

  useEffect(() => {
    if (loader || hydraulics?.loading) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader, hydraulics]);

  const configureChartConfig = () => {
    return [
      {
        name: 'Summary',
        key: 'summary',
        hide: false,
        columnFields: [
          {
            field: 'ann_mw',
            headerName: `MW (${depthPoints?.ann_mw?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                MW ({depthPoints?.ann_mw?.label})
              </Typography>
            </Box>
            ),
            width: 90,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.ann_mw?.decimals);
            },
          },
          {
            field: 'esd_with_sbp',
            headerName: `ESD (${depthPoints?.esd_with_sbp?.label})`,
            renderHeader: () => (
            <Box>
               <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ESD (Connection)
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                 ({depthPoints?.esd_with_sbp?.label})
              </Typography>
            </Box>
            ),
            width: 130,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.esd_with_sbp.decimals);
            },
          },
          {
            field: 'ecd_with_sbp',
            headerName: `ECD (${depthPoints?.ecd_with_sbp?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ECD (Drilling)
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ({depthPoints?.ecd_with_sbp?.label})
              </Typography>
            </Box>
            ),
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.ecd_with_sbp?.decimals);
            },
          },
          {
            field: 'ann_p_with_sbp',
            headerName: `Annular Pressure (${depthPoints?.ann_p_with_sbp?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Annular
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Pressure ({depthPoints?.ann_p_with_sbp?.label})
              </Typography>
            </Box>
            ),
            width: 120,
            headerAlign: 'center',
            align: 'center',
          }
        ]
      },
      {
        name: 'ESD w/o SBP',
        key: 'esd',
        color: HYDRAULICS_CHART_COLORS.ESD,
        hide: false,
        tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ESD w/o SBP: {x} ${depthPoints?.esd?.label}`,
      },
      {
        name: 'ESD (Drilling)',
        key: 'esd_with_current_sbp',
        color: HYDRAULICS_CHART_COLORS.ESD,
        hide: false,
        tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ESD (Drilling): {x} ${depthPoints?.esd_with_current_sbp?.label}`,
      },
      {
        name: 'ESD (Connection)',
        key: 'esd_with_sbp',
        color: HYDRAULICS_CHART_COLORS.ESD_WITH_SBP,
        hide: false,
        tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ESD (Connection): {x} ${depthPoints?.esd_with_sbp?.label}`,
        columnFields: [
          {
            field: 'ann_mw',
            headerName: `MW (${depthPoints?.ann_mw?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                MW ({depthPoints?.ann_mw?.label})
              </Typography>
            </Box>
            ),
            width: 90,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.ann_mw?.decimals);
            },
          },
          {
            field: 'esd',
            headerName: `ESD w/o SBP (${depthPoints?.esd?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ESD w/o
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                SBP ({depthPoints?.esd?.label})
              </Typography>
            </Box>
            ),
            width: 140,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.esd?.decimals);
            },
          },
          {
            field: 'esd_with_sbp',
            headerName: `ESD (${depthPoints?.esd_with_sbp?.label})`,
            renderHeader: () => (
            <Box>
               <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ESD (Connection)
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                 ({depthPoints?.esd_with_sbp?.label})
              </Typography>
            </Box>
            ),
            width: 130,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.esd_with_sbp.decimals);
            },
          },
        ]
      },
      {
        name: 'ECD w/o SBP',
        key: 'ecd',
        color: HYDRAULICS_CHART_COLORS.ECD,
        hide: false,
        tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ECD w/o SBP: {x} ${depthPoints?.ecd?.label}`,
      },
      {
        name: 'ECD (Drilling)',
        key: 'ecd_with_sbp',
        color: HYDRAULICS_CHART_COLORS.ECD_WITH_SBP,
        hide: false,
        tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ECD (Drilling): {x} ${depthPoints?.ecd?.label}`,
        columnFields: [
          {
            field: 'ann_mw',
            headerName: `MW (${depthPoints?.ann_mw?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                MW ({depthPoints?.ann_mw?.label})
              </Typography>
            </Box>
            ),
            width: 90,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.ann_mw?.decimals);
            },
          },
          {
            field: 'ecd',
            headerName: `ESD w/o SBP (${depthPoints?.ecd?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ECD w/o
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                SBP ({depthPoints?.ecd?.label})
              </Typography>
            </Box>
            ),
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.ecd?.decimals);
            },
          },
          {
            field: 'ecd_with_sbp',
            headerName: `ECD (${depthPoints?.ecd_with_sbp?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ECD (Drilling)
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ({depthPoints?.ecd_with_sbp?.label})
              </Typography>
            </Box>
            ),
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(depthPoints?.ecd_with_sbp?.decimals);
            },
          },
        ],
      },
      {
        name: 'PWD',
        key: 'pwd_ecd',
        color: HYDRAULICS_CHART_COLORS.PWD_ECD,
        hide: true,
        tooltip: `MD: {y} ${pwd?.md?.label} <br/> PWD Pressure: {pwd_p} ${pwd?.pwd_p?.label} <br/> PWD ECD: {x} ${pwd?.pwd_ecd?.label} <br/> Model Pressure: {model_p} ${pwd?.model_p?.label} <br/> Model ECD: {model_ecd} ${pwd?.model_ecd?.label} <br/> Pressure Diff: {p_diff} ${pwd?.p_diff?.label} <br/> APL Factor: {apl_factor}`,
        columnFields: [
          {
            field: 'pwd_p',
            headerName: `PWD Pressure (${pwd?.pwd_p?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                PWD Pressure
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ({pwd?.pwd_p?.label})
              </Typography>
            </Box>
            ),
            width: 105,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(pwd?.pwd_p?.decimals);
            },
          },
          {
            field: 'pwd_ecd',
            headerName: `PWD ECD ${pwd?.pwd_ecd?.label}`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                PWD ECD
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ({pwd?.pwd_ecd?.label})
              </Typography>
            </Box>
            ),
            width: 80,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(pwd?.pwd_ecd?.decimals);
            },
          },
          {
            field: 'model_p',
            headerName: `Model Pressure (${pwd?.model_p?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Model Pressure
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ({pwd?.model_p?.label})
              </Typography>
            </Box>
            ),
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(pwd?.model_p?.decimals);
            },
          },
          {
            field: 'model_ecd',
            headerName: `Model ECD (${pwd?.model_ecd?.label})`,
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Model
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                ECD ({pwd?.model_ecd?.label})
              </Typography>
            </Box>
            ),
            width: 95,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return '';
              }
              const value = Number(params.value);
              return isNaN(value) ? params.value : value.toFixed(pwd?.model_ecd?.decimals);
            },
          },
          {
            field: 'p_diff',
            headerName: 'Pressure Diff',
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Pressure
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Diff ({pwd?.p_diff?.label})
              </Typography>
            </Box>
            ),
            width: 80,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'apl_factor',
            headerName: 'APL Factor',
            renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                APL
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                Factor
              </Typography>
            </Box>
            ),
            width: 60,
            headerAlign: 'center',
            align: 'center',
          },
        ],
      },
      {
        name: 'PP',
        key: 'pp',
        color: HYDRAULICS_CHART_COLORS.PORE_PRESSURE,
        hide: false,
        tooltip: `MD: {y} ${window?.units?.md?.label} <br/> PP: {x} ${window?.units?.pp?.label}`
      },
      {
        name: 'FP',
        key: 'fp',
        color: HYDRAULICS_CHART_COLORS.FRACTURE_PRESSURE,
        hide: false,
        tooltip: `MD: {y} ${window?.units?.md?.label} <br/> FP: {x} ${window?.units?.fp?.label}`
      },
      {
        name: 'MW',
        key: 'ann_mw',
        color: HYDRAULICS_CHART_COLORS.MW,
        hide: false,
        tooltip: `MD: {y} ${window?.units?.md?.label} <br/> MW: {x} ${depthPoints?.ann_mw?.label}`
      },
    ];
  };

  const [chartConfig, setChartConfig] = useState([]);

  useEffect(() => {
    if (!hydraulicsRunResults.loading) {
      setChartConfig(configureChartConfig());
    }
  }, [hydraulicsRunResults.loading]);

  const generateWindowDataPoints = (config) => {
    const dataPoints = [];

    if (window?.data.length === 1) {
      const singleDataPoint = window?.data[0];
      dataPoints.push(
        { x: singleDataPoint[config.key], y: 0, markerType: 'none' },
        { x: singleDataPoint[config.key], y: singleDataPoint.md },
        { x: singleDataPoint[config.key], y: chartData?.at(-1)?.md, markerType: 'none' }
      );
    } else if (window?.data.length > 1) {
      const firstPoint = window?.data[0];
      const lastPoint = window?.data.at(-1);

      if (firstPoint.md > 0) {
        dataPoints.push({ x: firstPoint[config.key], y: 0, markerType: 'none' });
      }

      window?.data.forEach(item => {
        dataPoints.push({ x: item[config.key], y: item.md });
      });

      dataPoints.push({ x: lastPoint[config.key], y: chartData?.at(-1)?.md, markerType: 'none' });
    }
    return dataPoints;
  };

  const generatePWDDataPoints = (config) => {
    if (!Array.isArray(pwdData) || !pwdData?.length) return [];
    return pwdData.map(item => ({
      x: item[config.key],
      y: item.md,
      pwd_p: item.pwd_p,
      pwd_ecd: item.pwd_ecd,
      model_p: item.model_p,
      model_ecd: item.model_ecd,
      p_diff: item?.p_diff || '',
      apl_factor: item.apl_factor,
      esd: item.esd
    }));
  };

  const buildKeysToCheck = (config) => {
    const EXCLUDED_FIELDS = new Set(['md', 'ann_p_with_sbp']);
    const dynamicKeys = config?.columnFields?.map(col => col.field).filter(field => !EXCLUDED_FIELDS.has(field)) ?? [];
    const pwdExtraKeys = config?.name === 'PWD' ? ['esd_with_current_sbp', 'ecd_with_sbp'] : [];

    return Array.from(
      new Set([...dynamicKeys, ...pwdExtraKeys, 'pp', 'fp'])
    );
  };

  const generateViewportMinMax = () => {
    let minMaxData = [...chartData, ...window.data];
    const keysToCheck = buildKeysToCheck(charttable);

    if (charttable?.name === 'PWD' && Array.isArray(pwdData)) {
      const pwdOnlyData = pwdData.map(({ pwd_ecd }) => ({ pwd_ecd }));
      minMaxData = [...minMaxData, ...pwdOnlyData];
    }

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };

    return results;
  };

  useEffect(() => {
    if (!chartConfig.length || showTable || !chartData.length) return;
    const hasWindowData = window?.data?.length > 0;
    const hasPWDData = hydraulicsRunResults?.data?.results?.pwd?.length > 0;

    const updatedChartConfig = chartConfig.filter(item => {
      const shouldRemoveWindowKeys = !hasWindowData && (item.key === 'pp' || item.key === 'fp');

      const shouldRemovePWD = !hasPWDData && item.key === 'pwd_ecd';

      return !(shouldRemoveWindowKeys || shouldRemovePWD);
    });

    const baseColumnFields = [
      {
        field: 'md',
        headerName: `MD (${depthPoints?.md?.label})`,
        headerAlign: 'center',
        width: 70,
        align: 'center',
        renderHeader: () => (
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
              MD ({depthPoints?.md?.label})
            </Typography>
          </Box>
        ),
      }
    ];

    const chartConfigData = updatedChartConfig.map(item => ({
      ...item,
      columnFields: [...baseColumnFields, ...(item.columnFields || [])]
    }));

    const dropDownMenu = chartConfigData.filter(item => !['fp', 'pp', 'esd', 'ecd', 'ann_mw', 'esd_with_current_sbp'].includes(item.key));
    setSelectionMenu(updateSelectionNames(dropDownMenu));
    setCharttable(chartConfigData[0]);
    setChartConfig(getMatchingConfigsBySelectedKey(configureChartConfig(), chartConfigData[0]));
    setShowTable(true);
  }, [chartConfig.length, chartData]);

  useEffect(() => {
    if (!charttable || !chartData.length) return;

    const modifiedChartConfig = configureChartConfig().map(item => {
      const isPwdEcd = charttable.key === 'pwd_ecd';

      if (isPwdEcd) {
        if (item.key === 'pwd_ecd') {
          return { ...item, hide: false };
        }
        return item;
      }
      return { ...item, hide: item.key === 'pwd_ecd' };
    });

    setChartConfig(getMatchingConfigsBySelectedKey(modifiedChartConfig, charttable));
    getTableDatas(charttable);
  }, [charttable, chartData]);

  const extractValues = (dataArray, config) => {
    return dataArray.map(item => {
      const extracted = config?.columnFields?.reduce((acc, column) => {
        const field = column.field;
        if (item[field] !== undefined) {
          acc[field] = item[field];
        }
        return acc;
      }, {});
      extracted._id = Math.random().toString(36).substring(2, 10);
      return extracted;
    });
  };

  const getTableDatas = (charttable) => {
    const sourceData = charttable?.key === 'pwd_ecd' ? pwdData : chartData;
    const extractedValues = extractValues(sourceData, charttable);
    setTableRecord(extractedValues);
  };

  const CustomLegend = () => {
    const updateHide = (key, hideValue) => {
      setChartConfig(prevItems =>
        prevItems.map(item =>
          item.key === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    return hydraulicsRunResults.loading
      ? null
      : (
        <Grid container>
          {chartConfig
            .filter(item => {
              if (charttable?.key === 'pwd_ecd') {
                return true;
              }
              return item.key !== 'pwd_ecd';
            })
            .map(item => (
              <Box
                key={item.name}
                className={classes.legendItem}
                sx={{ opacity: item.hide ? 0.4 : 1 }}
                onClick={() => updateHide(item.key, !item.hide)}
              >
                {charttable?.key === 'pwd_ecd' && item.key === 'pwd_ecd'
                  ? (
                    <Square sx={{ color: item.color, mr: 1, fontSize: '1.3rem' }} />
                    )
                  : (
                    <LinearScaleIcon fontSize="large" sx={{ color: item.color, mr: 1 }} />
                    )}
                {item.name}
              </Box>
            ))}
        </Grid>
        );
  };

  const getValidValue = (val, fallback) =>
    val !== undefined && val !== null && val !== '' ? val : fallback;

  const { lowest, highest } = generateViewportMinMax();

  const xMin = getValidValue(hydraulics?.chartXAxis?.min, lowest - 0.1);
  const xMax = getValidValue(hydraulics?.chartXAxis?.max, highest + 0.1);
  const yMin = getValidValue(hydraulics?.chartYAxis?.min, null);
  const yMax = getValidValue(hydraulics?.chartYAxis?.max, null);

  const options = {
    theme: 'light2',
    animationEnabled: true,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    toolbar: {
      itemBackgroundColor: '#d3d3d3',
      itemBackgroundColorOnHover: '#3e3e3e',
      buttonBorderColor: '#3e3e3e',
    },
    backgroundColor: '#FFF',
    axisX: {
      viewportMinimum: xMin,
      viewportMaximum: xMax,
      interval: getOptimalInterval(xMin, xMax, 10),
      valueFormatString: '0.0',
      labelFontSize: 16,
      gridThickness: 1,
      title: `EMW (${depthPoints?.esd?.label})`,
      titleFontSize: 22,
    },
    axisY: {
      viewportMinimum: yMin,
      viewportMaximum: yMax,
      title: `MD (${depthPoints?.md?.label})`,
      titleFontSize: 22,
      labelFontSize: 16,
      includeZero: false,
      valueFormatString: '0',
      reversed: true,
      labelPlacement: 'outside',
      gridThickness: 1,
    },
    height: 900,
    legend: {
      fontSize: 20
    }
  };

  const handleChartAxisReset = (target) => {
    if (target === 'x' || target === 'resetAll') {
      dispatch({
        type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_XAXIS,
        payload: { min: '', max: '' },
      });
    }

    if (target === 'y' || target === 'resetAll') {
      dispatch({
        type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_YAXIS,
        payload: { min: '', max: '' },
      });
    }
  };

  const handleReportPdf = () => {
    const viewport = generateViewportMinMax();
    const mdRange = getMinMaxOfKey(chartData, 'md');
    const body = {
      inputs: tableList,
      jobId: data?.jobId,
      wellsInfoId: data?.wellsInfoId,
      runId: data?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: data?.name,
      type: charttable?.name,
      xAxis: {
        min: parseFloat(((xMin ?? (viewport?.lowest - 0.1))).toFixed(2)),
        max: parseFloat(((xMax ?? (viewport?.highest + 0.1))).toFixed(2)),
      },
      yAxis: {
        min: parseFloat(((yMin ?? Number(mdRange.min))).toFixed(2)),
        max: parseFloat(((yMax ?? Number(mdRange.max))).toFixed(2)),
      },
    };
    setLoader(true);
    dispatch(downloadECDESDChartPDF(body, reportPreview, () => {
      setLoader(false);
    }));
  };

  const handleDataReportDownload = () => {
    setLoader(true);
    const body = {
      jobId: data?.jobId,
      wellsInfoId: data?.wellsInfoId,
      runId: data?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: data?.name,
      type: HYDRAULICS_RUN_TYPES?.HYDRAULICS?.typeKey,
    };
    dispatch(downloadDataReport(body, () => {
      setLoader(false);
    }));
  };

  const getTargetData = () => {
    const decimals = hydraulicsRunResults?.units?.inputs?.target_data?.target_ecd?.decimals ?? 2;
    const anchorPoint = targetData?.anchor_point ?? '';
    const convertedECD = parseFloat(targetData?.target_ecd);
    const targetECD = Number.isFinite(convertedECD) ? convertedECD.toFixed(decimals) : '';
    return `${targetECD} ${hydraulicsRunResults?.units?.inputs?.target_data?.target_ecd?.label.toUpperCase()} @ ${anchorPoint?.replace(/_/g, ' ')?.toUpperCase()}`;
  };

  const getFormattedPressure = () => {
    const target_sbp = hydraulicsRunResults?.data.results?.summary?.target_sbp;
    const connection_sbp = hydraulicsRunResults?.data.results?.summary?.connection_sbp;
    const decimals_target = hydraulicsRunResults?.units?.results?.summary?.target_sbp?.decimals ?? 1;
    const decimals_connection = hydraulicsRunResults?.units?.results?.summary?.connection_sbp?.decimals ?? 1;
    if (target_sbp == null && connection_sbp == null) return '-';
    const formattedTarget = target_sbp == null ? '-' : unit !== UNIT_SYSTEM.US ? target_sbp : target_sbp.toFixed(decimals_target);
    const formattedConnection = connection_sbp == null ? '-' : unit !== UNIT_SYSTEM.US ? connection_sbp : connection_sbp.toFixed(decimals_connection);
    return `${formattedTarget} ${hydraulicsRunResults?.units?.results?.summary?.target_sbp?.label.toUpperCase()} DRILLING & ${formattedConnection} ${hydraulicsRunResults?.units?.results?.summary?.connection_sbp?.label.toUpperCase()} CONNECTION`;
  };

  const getFlowRateData = () => {
    const flowRate = hydraulicsRunResults?.data?.results?.inputs?.flow_in;
    const decimals = hydraulicsRunResults?.units?.results?.inputs?.flow_in?.decimals;
    if (flowRate == null) return '-';
    return unit !== UNIT_SYSTEM.US ? flowRate : flowRate.toFixed(decimals);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseClick();
        handleChartAxisReset('resetAll');
        setTableList([]);
      }}
      maxWidth={'xl'}
      fullWidth={true}
      style={{
        marginRight: '20px'
      }}
      PaperProps={{
        sx: {
          overflow: 'visible'
        },
      }}
    >
      {hydraulicsRunResults.loading || hydraulics?.loading
        ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '700px' }}
          >
            <CircularProgress />
          </Box>
          )
        : (
          <>
            <Box className={classes.dialogTitle}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    {`${wellDetails?.Rig} | ${wellDetails?.Well} | ${data?.name}`}
                  </Grid>
                  <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    {chartData?.length
                      ? <ReportsDownloadSelector
                          loading={loader || hydraulics.downloadESDECDChart === 'loading' || hydraulics?.loading}
                          onDataDownload={handleDataReportDownload}
                          onPreviewOpen={(previewItem) => {
                            setShowReportInputs(true);
                            setReportPreview(previewItem);
                          }}
                        />
                      : null}
                    <Tooltip title={TOOLTIPS.viewRunInputs}>
                      <span>
                        <IconButton
                          disabled={hydraulics.downloadESDECDChart === 'loading' || hydraulics?.loading}
                          onClick={() => {
                            dispatch(listHydraulicsDetails(rowData));
                            setShowHydraulicsParameterDialog({
                              open: true,
                              data: { ...rowData, results: hydraulicsRunResults?.data?.results },
                              dialogParameter: ''
                            });
                          }}>
                          <ShortcutOutlined fontSize='large' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      onClick={() => {
                        handleChartAxisReset('resetAll');
                        onCloseClick();
                        setTableList([]);
                      }}
                    >
                      <CancelPresentationRounded fontSize='large' />
                    </IconButton>
                  </Grid>
                </Grid>
              </Typography>
            </Box>
            <Divider />
            {chartData?.length > 0
              ? (
                <><Grid container item xs={showTable ? 7.7 : 12} marginTop={'17px'} alignItems="center" justifyContent="center">
                  <Grid item>
                    <CustomLegend />
                  </Grid>
                </Grid><Box style={{
                  overflow: 'auto',
                  marginTop: '-1.4%'
                }}>
                    <Grid container>
                      <Grid item xs={showTable ? 7.7 : 12} sx={{
                        p: 3,
                        pt: 4,
                        '.canvasjs-chart-toolbar': {
                          top: '-20px !important',
                        },
                        '.canvasjs-chart-container canvas': {
                          cursor: loader || hydraulics?.loading ? 'wait !important' : ''
                        }
                      }}>
                        <>
                          <CanvasJSChart
                            options={{
                              ...options,
                              data: chartConfig.map((config) => {
                                if (config.hide) return {};
                                if (config.key === 'pp' || config.key === 'fp') {
                                  return {
                                    type: 'line',
                                    name: config.name,
                                    showInLegend: false,
                                    toolTipContent: config.tooltip,
                                    dataPoints: generateWindowDataPoints(config),
                                    color: config.color,
                                  };
                                }
                                if (config.key === 'pwd_ecd') {
                                  return {
                                    type: 'scatter',
                                    name: config.name,
                                    markerType: 'square',
                                    showInLegend: false,
                                    toolTipContent: config.tooltip,
                                    dataPoints: generatePWDDataPoints(config),
                                    color: config.color,
                                  };
                                }
                                return {
                                  type: 'line',
                                  name: config.name,
                                  showInLegend: false,
                                  toolTipContent: config.tooltip,
                                  dataPoints: chartData?.map(item => ({
                                    x: item[config.key],
                                    y: item.md,
                                    ann_p: item?.ann_p,
                                    ann_p_with_sbp: item?.ann_p_with_sbp,
                                    esd_with_sbp: item?.esd_with_sbp,
                                    esd: item.esd
                                  })),
                                  color: config.color,
                                };
                              }),
                            }} />
                          <Box style={{ position: 'relative' }}>
                            <Box className={classes.xAxisEditBtn}>
                              <Box className={classes.editBox}>
                                <ChartsAxisPopover
                                  title='X Axis'
                                  defaultMin={generateViewportMinMax()?.lowest - 0.1}
                                  defaultMax={generateViewportMinMax()?.highest + 0.1}
                                  onReset={() => handleChartAxisReset('x')}
                                  onSubmit={(min, max) => {
                                    dispatch({
                                      type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_XAXIS,
                                      payload: { min: Number(min), max: Number(max) },
                                    });
                                  }} />
                              </Box>
                            </Box>

                            <Box className={classes.yAxisEditBtn}>
                              <Box className={classes.editBox}>
                                <ChartsAxisPopover
                                  title='Y Axis'
                                  defaultMin={getMinMaxOfKey(chartData, 'md').min}
                                  defaultMax={getMinMaxOfKey(chartData, 'md').max}
                                  onReset={() => handleChartAxisReset('y')}
                                  onSubmit={(min, max) => {
                                    dispatch({
                                      type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_YAXIS,
                                      payload: { min: Number(min), max: Number(max) },
                                    });
                                  }} />
                              </Box>
                            </Box>
                          </Box>
                        </>
                      </Grid>
                      {!showTable && <Box className={classes.menuTogglerBtn}>
                        <IconButton
                          onClick={() => {
                            setShowTable(!showTable);
                          }}>
                          <ChevronLeft sx={{ fontSize: '33px' }} />
                        </IconButton>
                      </Box>}
                      {showTable && <Grid item xs={4.3} marginTop={'10px'}>
                        <Box className={classes.menuTogglerTableBtn}>
                          <IconButton
                            onClick={() => {
                              setShowTable(!showTable);
                            }}>
                            <ChevronRight sx={{ fontSize: '33px' }} />
                          </IconButton>
                        </Box>
                        <Box display={'flex'} justifyContent={'start'} marginLeft={'1.25%'}>
                          <Grid item xs={3}>
                            <FormControl
                              fullWidth
                              sx={{ bgcolor: 'transparent', minWidth: '100px', p: 0, m: 0 }}
                            >
                              <InputLabel id={'filter-input-label'}>Filter</InputLabel>
                              <Select
                                labelId={'filter-select-id'}
                                id={'filter-id'}
                                value={charttable}
                                label='Filter'
                                onChange={(e) => setCharttable(e.target.value)}
                                sx={{
                                  color: charttable.color,
                                  fontWeight: 'bolder',
                                  fontSize: '18px',
                                  background: 'unset'
                                }}
                              >
                                {selectionMenu?.map((item, idItem) => (
                                  <MenuItem key={`item_${item?.name}_${idItem}`} value={item} sx={{ color: item?.color, fontSize: '18px' }} style={{ fontWeight: 'bold' }}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item sx={{ marginLeft: '20px' }}>
                            <Box className={classes.resultSummary}>
                              <Box>
                                {hydraulicsRunResults?.data.results?.inputs && (
                                  <Typography className={classes.resultSummaryText}>
                                    FLOW RATE = {getFlowRateData()} {hydraulicsRunResults?.units?.inputs?.flow_in?.label.toUpperCase()}
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <Typography className={classes.resultSummaryText}>
                                  TARGET = {isTargetDataEmpty ? 'NONE' : getTargetData()}
                                </Typography>
                              </Box>
                              <Box>
                                {hydraulicsRunResults?.data.results?.summary && (
                                  <Typography className={classes.resultSummaryText}>
                                    SBP = {getFormattedPressure()}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                          <Grid>
                            {hydraulicsRunResults?.data?.results?.pump_ramps?.length
                              ? <Tooltip title={TOOLTIPS.rampSchedule}>
                                <span>
                                  <IconButton
                                    disabled={hydraulics.downloadESDECDChart === 'loading' || hydraulics?.loading}
                                    onClick={() => setShowPumpRamp(true)}
                                  >
                                    <SignalCellularAlt sx={{ fontSize: 50 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              : null}
                          </Grid>
                        </Box>
                        {tableRecord && charttable?.columnFields && <Grid item xs={12}>
                          <Datagrid
                            fileName={`${wellDetails?.Well?.trim()}-${data?.name?.trim()}-${charttable?.name}`}
                            data={tableRecord}
                            sx={{
                              '.MuiDataGrid-cell': {
                                fontSize: '1rem',
                                padding: '2px',
                              },
                              '.MuiDataGrid-columnHeader': {
                                fontSize: '1.2rem',
                                fontWeight: '900 !important',
                              },
                              '& .MuiDataGrid-columnHeaderTitle': {
                                overflow: 'visible',
                                lineHeight: '1.43rem',
                                whiteSpace: 'normal',
                              },
                              '& .MuiDataGrid-footerContainer': {
                                minHeight: '0px'
                              },
                            }}
                            loading={false}
                            {...(tableRecord.length <= 100
                              ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
                              : {})}
                            columns={charttable?.columnFields}
                            disableStickyHeader={true}
                            autoHeight={false}
                            height={'870px'}
                            getRowHeight={() => 'auto'}
                            pageSize={100}
                            toolbar={{
                              options: {
                                columns: false,
                                filters: false,
                                density: false,
                                export: {
                                  enabled: true,
                                }
                              },
                            }} />
                        </Grid>}
                      </Grid>}
                    </Grid>
                  </Box></>)
              : <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '50vh',
                    textAlign: 'center',
                    p: 4,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
                  >
                    No Results Found
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.disabled' }}
                  >
                    Try adjusting parameters to generate results.
                  </Typography>
                </Box>
              </>}
            {
              showPumpRamp
                ? <PumpRampModel
                  wellDetails={wellDetails}
                  pumpRamps={{ ...rowData, results: hydraulicsRunResults?.data?.results }}
                  isOpen={showPumpRamp}
                  pumpRampUnits={hydraulicsRunResults?.units?.results?.pump_ramps}
                  onCloseClick={() => {
                    setShowPumpRamp(false);
                  }}
                  tableList={tableList}
                  setTableList={setTableList}
                  hydraulicWellsDetails={hydraulicWellsDetails}
                  phVersion={hydraulicsRunResults?.data?.results?.version}
                />
                : null
            }
            {
              showReportInput
                ? <ReportInputsModal
                  isOpen={showReportInput}
                  tableList={tableList}
                  setTableList={setTableList}
                  onCloseClick={() => {
                    setShowReportInputs(false);
                  }}
                  generateReport={handleReportPdf}
                />
                : null
            }
          </>
          )}
          {showHydraulicsParameterDialog.open && showHydraulicsParameterDialog?.data?.name
            ? (
                <HydraulicsDetailsModal
                  selectedJob={wellDetails}
                  isOpen={showHydraulicsParameterDialog.open}
                  onCloseClick={() => {
                    setShowHydraulicsParameterDialog({
                      open: false,
                      data: {}
                    });
                    dispatch({
                      type: REDUX_ACTIONS.HYDRAULICS_SET_ID,
                      payload: {
                        data: '',
                      },
                    });
                  }}
                  data={showHydraulicsParameterDialog.data}
                  dialogParameter={showHydraulicsParameterDialog.dialogParameter}
                  wellDetails={hydraulicWellsDetails}
                />
              )
            : null}
          <VersionText version={hydraulicsRunResults?.data?.results?.version} loading={hydraulicsRunResults.loading} />
    </Dialog>
  );
}

DepthVsDensityChartDialog.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  data: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  wellDetails: PropTypes.any,
  rowData: PropTypes.any,
  hydraulicWellsDetails: PropTypes.any
};

export default DepthVsDensityChartDialog;
